/**
 * TextOnImage.jsx
 * Putting a hook line on a picture, before it is resized for anywhere.
 *
 * The preview is drawn with the same code as the final render, at a smaller
 * size, because everything is measured as a fraction of the width. What you
 * see really is what gets written.
 *
 * The guides matter as much as the text. TikTok covers the bottom quarter and
 * the right-hand edge of the screen with its own buttons and caption, so text
 * placed there vanishes behind the interface — which is only ever discovered
 * after posting.
 */

import { useEffect, useRef, useState } from 'react';
import {
  POSITIONS,
  SAFE_AREAS,
  SHADES,
  TEXT_SIZES,
  drawTextOn,
  renderTextImage,
} from '../utils/textimage.js';
import { Check, Warning } from './Icons.jsx';

const PREVIEW_WIDTH = 380;

export default function TextOnImage({ source, onClose, onDone }) {
  const [headline, setHeadline] = useState('');
  const [sub, setSub] = useState('');
  const [position, setPosition] = useState('bottom');
  const [size, setSize] = useState('medium');
  const [colour, setColour] = useState('#ffffff');
  const [shade, setShade] = useState('dark');
  const [align, setAlign] = useState('left');
  const [areaKey, setAreaKey] = useState('tiktok');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canvas = useRef(null);
  const bitmap = useRef(null);

  // Loaded once; every preview redraw reuses it.
  useEffect(() => {
    let dead = false;
    createImageBitmap(source.file)
      .then((b) => {
        if (dead) b.close?.();
        else bitmap.current = b;
      })
      .catch(() => setError(new Error('That picture could not be opened.')));

    return () => {
      dead = true;
      bitmap.current?.close?.();
      bitmap.current = null;
    };
  }, [source]);

  // Redrawn on every change. Small enough that it is instant.
  useEffect(() => {
    const el = canvas.current;
    const image = bitmap.current;
    if (!el || !image) return;

    const scale = PREVIEW_WIDTH / image.width;
    el.width = PREVIEW_WIDTH;
    el.height = Math.round(image.height * scale);

    const ctx = el.getContext('2d');
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.drawImage(image, 0, 0, el.width, el.height);

    drawTextOn(ctx, {
      width: el.width,
      height: el.height,
      headline,
      sub,
      position,
      size,
      colour,
      shade,
      align,
      area: SAFE_AREAS[areaKey],
      guides: areaKey !== 'none',
    });
  });

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      const file = await renderTextImage(source.file, {
        headline,
        sub,
        position,
        size,
        colour,
        shade,
        align,
        area: SAFE_AREAS[areaKey],
      });
      onDone(file);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  const nothingToDraw = !headline.trim() && !sub.trim();

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <h2>Text on the picture</h2>
        <p className="hint">
          The original is left exactly as it is. This makes a new picture, which you can then
          resize and save like any other.
        </p>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <div className="text-tool">
          <div className="text-tool__preview">
            <canvas ref={canvas} />
            {areaKey !== 'none' && (
              <p className="hint">
                Keep the text inside the dashed line — {SAFE_AREAS[areaKey].label} covers the rest
                with its own buttons and caption.
              </p>
            )}
          </div>

          <div className="text-tool__controls">
            <div className="field">
              <label htmlFor="txt-headline">Hook</label>
              <textarea
                id="txt-headline"
                rows={2}
                value={headline}
                placeholder="Stop typing. Start talking."
                onChange={(e) => setHeadline(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="txt-sub">Second line (optional)</label>
              <input
                id="txt-sub"
                value={sub}
                placeholder="Free for 14 days"
                onChange={(e) => setSub(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Where it sits</label>
              <div className="btn-row">
                {Object.entries(POSITIONS).map(([key, label]) => (
                  <button
                    key={key}
                    className={`btn btn--sm ${position === key ? 'btn--primary' : ''}`}
                    onClick={() => setPosition(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Size</label>
              <div className="btn-row">
                {Object.entries(TEXT_SIZES).map(([key, s]) => (
                  <button
                    key={key}
                    className={`btn btn--sm ${size === key ? 'btn--primary' : ''}`}
                    onClick={() => setSize(key)}
                  >
                    {s.label}
                  </button>
                ))}
                <button
                  className={`btn btn--sm ${align === 'centre' ? 'btn--primary' : ''}`}
                  onClick={() => setAlign(align === 'centre' ? 'left' : 'centre')}
                  title="Centre the text"
                >
                  Centred
                </button>
              </div>
            </div>

            <div className="field">
              <label>Behind the text</label>
              <div className="btn-row">
                {Object.entries(SHADES).map(([key, label]) => (
                  <button
                    key={key}
                    className={`btn btn--sm ${shade === key ? 'btn--primary' : ''}`}
                    onClick={() => setShade(key)}
                  >
                    {label}
                  </button>
                ))}
                <input
                  type="color"
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  title="Text colour"
                />
              </div>
            </div>

            <div className="field">
              <label>Keep clear of</label>
              <div className="btn-row">
                {Object.entries(SAFE_AREAS).map(([key, a]) => (
                  <button
                    key={key}
                    className={`btn btn--sm ${areaKey === key ? 'btn--primary' : ''}`}
                    onClick={() => setAreaKey(key)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="modal__actions">
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={apply} disabled={busy || nothingToDraw}>
            <Check size={15} /> {busy ? 'Making it…' : 'Add this picture'}
          </button>
        </div>
      </div>
    </div>
  );
}
